import { useEffect, useMemo } from "react"
import { observer } from "mobx-react"
import { useParams } from "react-router-dom"

import { t } from "@lingui/macro"
import { Grid, Typography } from "@mui/material"

import {
    getContractDataList,
    getObjectDataList,
} from "src/views/contracts/utils/contractDataUtils"

import { ContractDetailStore } from "src/views/contracts/pages/details/store"
import { ContractHeader } from "src/views/contracts/pages/details/components/ContractHeader"
import { TenantsList } from "src/views/contracts/pages/details/components/TenantsList"
import { ContractDetails } from "src/views/contracts/pages/details/components/ContractDetails"

import { useContractDetail } from "src/views/contracts/pages/details/hooks/useContractDetail"

import { StyledContractDetails } from "src/views/contracts/pages/details/styled"

import { useStore } from "src/store/lib/useStore"
import { StoreProvider } from "src/store/lib/StoreProvider"

import { H1 } from "src/components/PageHeader"
import { GlobalStore } from "src/store"
import { CenteredSpinner } from "src/components/CenteredSpinner"

const View = observer(() => {
    const { id } = useParams<{ id: string }>()
    const store = useStore(ContractDetailStore)
    const globalStore = useStore(GlobalStore)

    useEffect(() => {
        if (id != null) {
            void store.init(parseInt(id, 10))
        }

        return () => {
            store.dispose()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const { contract, handleBackClick, handleSimulateTenant } =
        useContractDetail()

    const tenants = useMemo(() => contract?.tenants ?? [], [contract?.tenants])

    const contractDetailsLoading = globalStore.loading.is(
        ContractDetailStore.LoadingKeys.init,
    )

    const renderBreadcrumbs = useMemo(
        () => (
            <StyledContractDetails.Header.Wrapper>
                <H1>{t`contract-details.contracts`}</H1>
                <StyledContractDetails.BreadcrumbContainer>
                    <StyledContractDetails.BackCursor>
                        <Typography variant="body1" color="#737373">
                            {t`contract-details.contracts` + ` / `}
                        </Typography>
                    </StyledContractDetails.BackCursor>
                    <Typography variant="body1" color="#333333">
                        {String(contract?.lease_contract_id ?? "")}
                    </Typography>
                </StyledContractDetails.BreadcrumbContainer>
            </StyledContractDetails.Header.Wrapper>
        ),
        [contract?.lease_contract_id],
    )

    const renderContractContent = useMemo(() => {
        const contractDataList = getContractDataList()
        const objectDataList = getObjectDataList()

        return (
            <StyledContractDetails.TabsWrapper>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <ContractDetails
                            title={t`contract-details.contract-information`}
                            dataList={contractDataList}
                            data={contract}
                        />
                        <ContractDetails
                            title={t`contract-details.object-information`}
                            dataList={objectDataList}
                            data={contract}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TenantsList
                            tenants={tenants}
                            onSimulateTenant={handleSimulateTenant}
                        />
                    </Grid>
                </Grid>
            </StyledContractDetails.TabsWrapper>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contract, tenants])

    return (
        <StyledContractDetails.Wrapper>
            {renderBreadcrumbs}
            <ContractHeader contract={contract} onBackClick={handleBackClick} />
            {contractDetailsLoading ? (
                <CenteredSpinner height="500px" />
            ) : Boolean(store.recordNotFound) ? (
                <StyledContractDetails.NotFound.Container>
                    <StyledContractDetails.NotFound.Title variant="h5">
                        {t`contract-details.record-not-found`}
                    </StyledContractDetails.NotFound.Title>
                    <StyledContractDetails.NotFound.Description variant="body1">
                        {t`contract-details.record-not-found-description`}
                    </StyledContractDetails.NotFound.Description>
                </StyledContractDetails.NotFound.Container>
            ) : (
                renderContractContent
            )}
        </StyledContractDetails.Wrapper>
    )
})

export const ContractDetailView = () => (
    <StoreProvider Store={ContractDetailStore}>
        <View />
    </StoreProvider>
)
